/* eslint-disable complexity */
import './entry-group-step2.scss'

import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getGroupInitialValues } from 'src/containers/data-entry/handlers/form-initial-values'
import { useValidationSchemasGroup1 } from 'src/containers/data-entry/handlers/validation-schema-group.hook'
import { lowerLanguage } from 'src/i18n'
import { SAGA_GUARDS } from 'src/navigation'
import { shoot } from 'src/redux/actions'
import { select_group, select_history_item, select_loading } from 'src/redux/store'
import { document_t, gender_t, InputRadioOption, nationality_t } from 'src/types'
import { Formik } from 'src/types/formik'
import { FormikGroupStep2, FormikMainInsuredGroup, GroupFile } from 'src/types/formik/group.types'
import { group_t } from 'src/types/http/group.types'
import { useBoolean } from 'src/utils'

export const useEntryGroupStep2 = ({
	formikFather,
}: {
	formikFather: Formik<FormikMainInsuredGroup>
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const { setFieldValue } = formikFather

	// Constants
	const nationalityOptions = [
		{ id: 'IT', value: t('data_entry_step_2.insured_nationality_italy') },
		{ id: 'EXT', value: t('data_entry_step_2.insured_nationality_foreign') },
	]

	const fcQuestionOptions = [
		{ id: 'yes_id', value: t('data_entry_step_2.insured_fc_question_yes') },
		{ id: 'no_id', value: t('data_entry_step_2.insured_fc_question_no') },
	]

	const genderOptions: InputRadioOption[] = [
		{
			id: 'M',
			value: t('data_entry_step_2.insured_gender_male'),
		},
		{
			id: 'F',
			value: t('data_entry_step_2.insured_gender_female'),
		},
	]

	// Selectors
	const historyDocumentsGroup: document_t[] = useSelector(select_history_item("history_documents_group"));
	const historyGroup = useSelector(select_history_item('history_group'));
	const loadingGroup = useSelector(select_loading(SAGA_GUARDS.GROUP))
	const group = useSelector(select_group);

	// States
	const [filesGroup, setFilesGroup] = useState<File[]>([])
	const [isModalVisible, showModal, hideModal] = useBoolean(false)
	const [initalValueFormikModal, saveFormikModal] = useState<FormikGroupStep2>({
		id: 0,
		name: '',
		surname: '',
		nationality: '',
		fiscalCodeQuestion: '',
		birthDate: '',
		birthNation: '',
		birthPlace: '',
		gender: '',
		fiscalCode: '',
	})

	////////////////////////////////////////////////////////////////////////////////////////////

	// useEffect
	useEffect(() => {
		setFieldValue('group', group?.group);
		let status: 'OK' | 'PARTIAL_ERRORS' | 'ERRORS' | undefined = undefined
		if (!group?.hasErrors) {
			status = 'OK'
		} else if (group?.hasErrors === 'PARTIAL_ERRORS') {
			status = 'PARTIAL_ERRORS'
		} else if (group?.hasErrors === 'ERRORS') {
			status = 'ERRORS'
		}
		setFieldValue('fileStatus', status);
		dispatch(shoot.reduce_history_item('history_group', {
			...historyGroup,
			group: group?.group,
			fileStatus: status
		}))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [group])

	useEffect(() => {
		if (filesGroup && filesGroup.length > 0) {
			setTimeout(async () => {
				let groupFiles: GroupFile[] = []
				await Promise.all(
					Object?.entries(filesGroup)?.map(async ([, file]) => {
						const file_ = await formatFile(file)
						groupFiles = [...groupFiles, file_]
					})
				)

				const orderedGroupFiles = groupFiles?.sort((a, b) => a?.name?.localeCompare(b?.name))
				setFieldValue('fileNames', orderedGroupFiles?.map((item) => item?.name) || [])
				dispatch(shoot.reduce_history_item('history_group', {
					...historyGroup,
					fileNames: orderedGroupFiles?.map((item) => item?.name) || []
				}))

				const data_: group_t = {
					groupFiles: orderedGroupFiles,
					language: lowerLanguage
				};

				dispatch(shoot.saga_group(data_));
			}, 1000)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filesGroup])

	////////////////////////////////////////////////////////////////////////////////////////////

	// Formik
	const validationSchema = useValidationSchemasGroup1()

	const formikModal = useFormik<FormikGroupStep2>({
		initialValues: initalValueFormikModal,
		validationSchema: validationSchema,
		onSubmit: (values, { setTouched, setSubmitting }) => {
			setTouched({})

			saveFormikModal(values)

			const updatedGroup = [...(formikFather?.values?.group || {})]
			const updatedGroupIndex = updatedGroup.findIndex(group => group.id === values.id)
			updatedGroup.splice(updatedGroupIndex, 1, {
				id: values.id,
				name: values?.name || '',
				surname: values?.surname || '',
				nationality: (values?.nationality as nationality_t) || '',
				birthDate: values?.birthDate || '',
				birthNation: values?.birthNation || '',
				birthPlace: values?.birthPlace || '',
				gender: (values?.gender as gender_t) || '',
				fiscalCode: values?.fiscalCode || '',
				fiscalCodeQuestion: values?.fiscalCodeQuestion || '',
			})

			setFieldValue('group', updatedGroup)
			dispatch(shoot.reduce_history_item('history_group', {
				...historyGroup,
				group: updatedGroup
			}))

			hideModal()

			setSubmitting(false)
		},
	})

	////////////////////////////////////////////////////////////////////////////////////////////

	// Functions
	const onDownloadFile = () => {
		if (historyDocumentsGroup?.[0]?.file)
			window.open(historyDocumentsGroup?.[0]?.file[lowerLanguage], '_blank')
	}

	const formatFile = async (file: File): Promise<GroupFile> => {
		const formattedFile: GroupFile = await new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.onload = async () => {
				resolve({
					name: file?.name || '',
					size: file?.size || 0,
					data: reader.result?.toString() || '',
					type: file?.type || '',
					lastModified: file?.lastModified || 0,
				})
			}
			reader.onerror = reject
			reader.readAsDataURL(file)
		})

		return formattedFile
	}

	const onShowModalEditPerson = (id?: number) => {
		if (id !== null && id !== undefined) {
			showModal()
			const item = formikFather?.values?.group?.find(g => g.id === id)

			formikModal?.setFieldValue('id', item?.id)
			formikModal?.setFieldValue('name', item?.name || '')
			formikModal?.setFieldValue('surname', item?.surname || '')
			formikModal?.setFieldValue('fiscalCodeQuestion', item?.fiscalCode ? 'yes_id' : 'no_id')
			formikModal?.setFieldValue('nationality', item?.nationality || '')
			formikModal?.setFieldValue('birthDate', item?.birthDate || '')
			formikModal?.setFieldValue('birthNation', item?.birthNation || '')
			formikModal?.setFieldValue('birthPlace', item?.birthPlace || '')
			formikModal?.setFieldValue('gender', item?.gender || '')
			formikModal?.setFieldValue('fiscalCode', item?.fiscalCode || '')
		}
	}

	const onRemoveFile = () => {
		dispatch(shoot.reduce_group([]));
		dispatch(shoot.reduce_evaluate_premium());
		dispatch(shoot.reduce_history_item("history_group", getGroupInitialValues));

		setFieldValue('group', [])
		setFieldValue('fileStatus', undefined)
		setFieldValue('fileNames', [])
		setFieldValue("mainInsured", getGroupInitialValues.mainInsured);
	}

	return {
		t,
		filesGroup,
		formikModal,
		genderOptions,
		isModalVisible,
		validationSchema,
		fcQuestionOptions,
		nationalityOptions,
		loadingGroup,
		hideModal,
		onRemoveFile,
		setFilesGroup,
		onDownloadFile,
		onShowModalEditPerson,
	}
}
